import {
	type AlertAck,
	ApiError,
	type CodTenderOptions,
	type DeliveryDriver,
	type DriverRun,
	type DriverShipment,
	type DriverWallet,
	type SyncPullResult,
} from "@khazzin/api-client";
import {
	drain,
	type OutboxEntry,
	type OutboxStore,
	type SyncLoop,
	startSyncLoop,
} from "@khazzin/api-client/sync";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useApi } from "../api";
import { ACK, ackPayload, awaiting } from "../modules/alerts/alerts";
import { pushToken } from "../modules/push/push";
import { all, del, get, purgeDatabase, put } from "./db";
import { reserveMedia, uploadMedia } from "./media";

/**
 * The Driver App's offline state (EP10-S15, `10/04` §3, §5): the cached day,
 * the outbox, and the sync cycle that joins them to the server.
 *
 * The UI never waits on the network. A command is written to the outbox
 * first, the cached day is updated optimistically, and the sync loop sends it
 * when it can — on start, on focus, on `online`, after every command, and
 * with backoff after a failure. A cycle drains the outbox and then pulls, so
 * the day it caches already reflects what was just applied.
 */

/** The cached day: the last pull, with shipments by id. */
export interface Day {
	run: DriverRun;
	driver: DeliveryDriver;
	shipments: Record<string, DriverShipment>;
}

/** Session facts kept beside the cursor. */
interface Meta {
	cursor: string;
	/** Whose data this is: another user signing in on a shared phone purges it. */
	userId: string;
	/** The last successful sync, which is also the last proof the session was good. */
	lastSyncAt: string;
}

/** Local data is purged after this long without a successful sync (`10/04` §3). */
export const AUTH_GRACE_MS = 7 * 24 * 60 * 60 * 1000;

const DEVICE_KEY = "khazzin.driver.deviceId";

/** This install's stable id. A reinstall, or a sign-out, starts a new one. */
export function deviceId(): string {
	try {
		let id = localStorage.getItem(DEVICE_KEY);
		if (id === null) {
			id = crypto.randomUUID();
			localStorage.setItem(DEVICE_KEY, id);
		}
		return id;
	} catch {
		// Storage refused (private mode): one id for the session is still unique.
		fallbackId ??= crypto.randomUUID();
		return fallbackId;
	}
}
let fallbackId: string | undefined;

/**
 * Applies a pull to the cached day. A snapshot replaces it; a delta replaces
 * the changed shipments. Either way a shipment that left the run (reassigned
 * away, delivered) leaves the cache.
 */
export function mergePull(previous: Day | undefined, pulled: SyncPullResult): Day {
	const shipments: Record<string, DriverShipment> =
		pulled.mode === "delta" && previous !== undefined ? { ...previous.shipments } : {};
	for (const s of pulled.shipments) {
		shipments[s.shipmentId] = s;
	}
	const keep = new Set(pulled.run.stopIds);
	for (const id of Object.keys(shipments)) {
		if (!keep.has(id)) {
			delete shipments[id];
		}
	}
	return { run: pulled.run, driver: pulled.driver, shipments };
}

/**
 * Whether a cached day has everything this build reads. A day cached by an
 * older build (before EP10-S17's order lines or EP10-S18's proof policy) is
 * pulled again whole: a delta would only refresh the stops that changed.
 */
export function current(day: Day | undefined): boolean {
	return (
		day !== undefined &&
		Object.values(day.shipments).every(
			(s) => Array.isArray(s.lines) && Array.isArray(s.podRequiredMethods),
		)
	);
}

/**
 * Whether cached data may still be used. With a session, only its owner's; a
 * session proves itself, so its age does not matter. Without one (offline, the
 * token cannot refresh), only within the grace since the last good sync.
 */
export function cacheUsable(meta: Meta | undefined, userId: string | null, now: number): boolean {
	if (meta === undefined) {
		return true;
	}
	if (userId !== null) {
		return meta.userId === userId;
	}
	return now - Date.parse(meta.lastSyncAt) <= AUTH_GRACE_MS;
}

const outboxStore: OutboxStore = {
	all: () => all<OutboxEntry>("outbox"),
	put: (e) => put("outbox", e.idempotencyKey, e),
	remove: (key) => del("outbox", key),
};

export interface CommandOptions {
	shipmentId?: string;
	/** The optimistic change to the cached day. */
	apply?: (day: Day) => Day;
}

interface Offline {
	ready: boolean;
	day: Day | null;
	outbox: OutboxEntry[];
	/**
	 * The driver's cash wallet as the last sync read it (EP10-S19): the
	 * collections the server holds. Queued confirmations are the outbox's.
	 */
	wallet: DriverWallet | null;
	/**
	 * The currencies the driver may take today and their rates (COD phase
	 * 2a), as the last sync read them: kept for a day with no signal.
	 */
	tenderOptions: CodTenderOptions | null;
	/**
	 * The alerts waiting for the driver's acknowledgment (EP12-S15), as the
	 * last sync read them, less those acknowledged on this phone since.
	 */
	alerts: AlertAck[];
	/** Queues the acknowledgment of an alert; the prompt goes at once. */
	ackAlert: (notificationId: string) => Promise<void>;
	online: boolean;
	syncing: boolean;
	lastSyncAt: string | null;
	/** Queues a command; resolves once it is durable on the phone. */
	command: (
		name: string,
		payload: Record<string, unknown>,
		options?: CommandOptions,
	) => Promise<void>;
	syncNow: () => void;
	/**
	 * Takes a command the server refused off the phone, once the driver has
	 * read why (ADJ-1022-4). The server keeps its own record of it.
	 */
	dismiss: (idempotencyKey: string) => Promise<void>;
	/** Unregisters the device, purges everything local, then signs out. */
	signOut: () => Promise<void>;
	/**
	 * Another driver's unsynced work, found on this phone when this driver
	 * signed in (ADJ-M03-4). Nothing loads and nothing syncs until the driver
	 * chooses: sign out so its owner can sync it, or {@link discardHeld}.
	 */
	held: PendingWork | null;
	/** Deletes the held work and carries on as the signed-in driver. */
	discardHeld: () => Promise<void>;
	/**
	 * Work deleted unsynced because the phone went seven days without a sync
	 * while signed out (ADJ-M03-4). The policy stands; the sign-in screen says
	 * what was lost, so the driver can tell dispatch.
	 */
	lost: PendingWork | null;
	/**
	 * The signed-in account has no driver profile: the pull answered 404 (or
	 * 403, no driver role). Nothing will ever load, so the shell says so instead of
	 * "Loading your day…". The loop keeps polling, so an account made a driver
	 * since recovers on its own.
	 */
	notDriver: boolean;
}

const OfflineContext = createContext<Offline | null>(null);

export function useOffline(): Offline {
	const value = useContext(OfflineContext);
	if (value === null) {
		throw new Error("useOffline must be used inside <OfflineProvider>");
	}
	return value;
}

/** A command, queued: `useCommand("shift_start")(payload)`. */
export function useCommand(name: string) {
	const { command } = useOffline();
	return useCallback(
		(payload: Record<string, unknown>, options?: CommandOptions) => command(name, payload, options),
		[command, name],
	);
}

/**
 * How long a guarded tap holds the guard at least: a double tap's second
 * press lands within it even when the write was quicker, and would otherwise
 * press the button that replaced the first (Start shift → End shift).
 */
export const GUARD_HOLD_MS = 500;

/**
 * A tap that queues something, guarded against a double tap (ADJ-M03-4):
 * `pending` is the button's busy state — which disables it — while the first
 * press is still writing and for at least {@link GUARD_HOLD_MS}, so a second
 * press lands on a disabled button. React commits a click's state before the
 * next click is dispatched, so the button is the whole guard.
 */
export function useGuarded(): [pending: boolean, run: (act: () => Promise<unknown>) => void] {
	const [pending, setPending] = useState(false);
	const run = useCallback((act: () => Promise<unknown>) => {
		setPending(true);
		const held = new Promise((resolve) => setTimeout(resolve, GUARD_HOLD_MS));
		void Promise.all([act(), held]).finally(() => setPending(false));
	}, []);
	return [pending, run];
}

/**
 * Removes every trace of the driver's data from this phone.
 *
 * The driver's data is all in IndexedDB. Cache Storage holds only the app's
 * own files (the service worker's precache), so it is left alone (ADJ-M03-4):
 * deleting it used to leave a phone that could not open the app again
 * without a signal.
 */
export async function purgeLocal(): Promise<void> {
	await purgeDatabase().catch(() => undefined);
	try {
		localStorage.removeItem(DEVICE_KEY);
	} catch {
		// Storage refused: there is nothing it could have kept.
	}
}

/** What is on the phone and not yet on the server (ADJ-M03-4). */
export interface PendingWork {
	/** Commands still queued. Conflicts and rejections are already the server's. */
	commands: number;
	/** Photos and signatures still to upload. */
	files: number;
}

export async function pendingWork(): Promise<PendingWork> {
	const [entries, files] = await Promise.all([outboxStore.all(), all("media")]);
	return { commands: entries.filter((e) => e.state === "queued").length, files: files.length };
}

export function hasWork(work: PendingWork): boolean {
	return work.commands > 0 || work.files > 0;
}

export interface OfflineProviderProps {
	/** The signed-in user, or null while signed out (offline, the session cannot refresh). */
	userId: string | null;
	/** The identity provider's sign-out, run last. */
	onSignedOut: () => void | Promise<void>;
	appVersion: string;
	children: ReactNode;
}

export function OfflineProvider({
	userId,
	onSignedOut,
	appVersion,
	children,
}: OfflineProviderProps) {
	const api = useApi();
	const [ready, setReady] = useState(false);
	const [day, setDay] = useState<Day | null>(null);
	const [outbox, setOutbox] = useState<OutboxEntry[]>([]);
	const [wallet, setWallet] = useState<DriverWallet | null>(null);
	const [tenderOptions, setTenderOptions] = useState<CodTenderOptions | null>(null);
	const [alerts, setAlerts] = useState<AlertAck[]>([]);
	const [online, setOnline] = useState(() => navigator.onLine);
	const [syncing, setSyncing] = useState(false);
	const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
	const [held, setHeld] = useState<PendingWork | null>(null);
	const [lost, setLost] = useState<PendingWork | null>(null);
	const [notDriver, setNotDriver] = useState(false);
	const loop = useRef<SyncLoop | null>(null);
	const dayRef = useRef<Day | null>(null);
	dayRef.current = day;

	const refreshOutbox = useCallback(async () => {
		setOutbox((await outboxStore.all()).sort((a, b) => a.seq - b.seq));
	}, []);

	useEffect(() => {
		const up = () => setOnline(true);
		const down = () => setOnline(false);
		window.addEventListener("online", up);
		window.addEventListener("offline", down);
		return () => {
			window.removeEventListener("online", up);
			window.removeEventListener("offline", down);
		};
	}, []);

	// Load the cache, purging it first when it is somebody else's or stale —
	// but never somebody else's unsynced work without asking (ADJ-M03-4).
	useEffect(() => {
		let live = true;
		void (async () => {
			try {
				const meta = await get<Meta>("cursor", "meta");
				if (!cacheUsable(meta, userId, Date.now())) {
					const work = await pendingWork();
					if (userId !== null && hasWork(work)) {
						// Another driver's: loaded for nobody, synced under nobody's token.
						if (live) {
							setHeld(work);
						}
						return;
					}
					if (hasWork(work) && live) {
						setLost(work);
					}
					await purgeLocal();
				}
				if (live) {
					setHeld(null);
				}
				const cached = await get<Day>("run", "today");
				if (!live) {
					return;
				}
				setDay(cached ?? null);
				setWallet((await get<DriverWallet>("run", "wallet")) ?? null);
				setTenderOptions((await get<CodTenderOptions>("run", "tenderOptions")) ?? null);
				setAlerts((await get<AlertAck[]>("run", "alerts")) ?? []);
				setLastSyncAt((await get<Meta>("cursor", "meta"))?.lastSyncAt ?? null);
				await refreshOutbox();
			} finally {
				if (live) {
					setReady(true);
				}
			}
		})();
		return () => {
			live = false;
		};
	}, [userId, refreshOutbox]);

	// The sync loop runs only with a session: without one there is no token.
	// Nor while another driver's work is held: it would push under this one's.
	useEffect(() => {
		if (!ready || userId === null || held !== null) {
			return;
		}
		const device = deviceId();
		// The push token last registered; null until the device is registered.
		let registered: string | undefined | null = null;
		const cycle = async () => {
			if (!navigator.onLine) {
				throw new Error("offline");
			}
			setSyncing(true);
			try {
				// EP10-S21: registered again whenever the token changes (granted,
				// rotated). Best effort: the day must sync regardless.
				const token = await pushToken(import.meta.env);
				if (registered === null || token !== registered) {
					const ok = await api
						.registerDevice({
							deviceId: device,
							interface: "driver",
							platform: "web",
							appVersion,
							...(token === undefined ? {} : { pushToken: token }),
						})
						.then(() => true)
						.catch(() => false);
					if (ok) {
						registered = token;
					}
				}
				// EP10-S18: a queued command's files are reserved first, so it
				// carries attachment ids rather than phone-local names; their
				// bytes follow the push, so the outcome never waits on them.
				await reserveMedia(api, outboxStore);
				const drained = await drain(
					outboxStore,
					async (mutations) => (await api.syncPush(device, mutations)).results,
				);
				// EP10-S19: the wallet is read before the outbox is, so a confirmation
				// that just synced leaves the queue and joins the wallet together —
				// the Cash tab's total never dips between the two. A failed read
				// keeps the last one rather than failing the day.
				const fresh = await api.getMyDriverWallet().catch(() => null);
				if (fresh !== null) {
					await put("run", "wallet", fresh);
					setWallet(fresh);
				}
				// COD phase 2a: today's currencies and rates, kept for an offline day.
				const options = (await api.getMyCodTenderOptions?.().catch(() => null)) ?? null;
				if (options !== null) {
					await put("run", "tenderOptions", options);
					setTenderOptions(options);
				}
				// EP12-S15: the alerts still waiting, read after the drain so an
				// acknowledgment just sent is gone; one tapped since is left out.
				const mine = await api.listMyAlerts().catch(() => null);
				if (mine !== null) {
					const open = awaiting(mine.items, await outboxStore.all());
					await put("run", "alerts", open);
					setAlerts(open);
				}
				await refreshOutbox();
				const uploaded = await uploadMedia(api);
				const meta = await get<Meta>("cursor", "meta");
				const cached = await get<Day>("run", "today");
				let pulled: SyncPullResult;
				try {
					pulled = await api.syncPull({
						scope: "driver_run_today",
						deviceId: device,
						...(meta?.cursor && current(cached) ? { cursor: meta.cursor } : {}),
					});
				} catch (err) {
					// Not a failure to back off from: the answer will not change until
					// somebody makes this account a driver, so poll at the usual pace.
					// The status, not the code: the server sends every 404 as a bare
					// not_found (handler.go), and the pull names no record in its path,
					// so its only 404 is fleet's "this account is not a driver". A 403
					// is an account without the driver role at all (ADJ-1022-4): the
					// same answer, not a day that loads forever.
					if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
						setNotDriver(true);
						return;
					}
					throw err;
				}
				setNotDriver(false);
				const next = mergePull(cached ?? undefined, pulled);
				const synced = new Date().toISOString();
				await put("run", "today", next);
				await put("cursor", "meta", {
					cursor: pulled.cursor,
					userId,
					lastSyncAt: synced,
				} satisfies Meta);
				setDay(next);
				setLastSyncAt(synced);
				if (drained.retry) {
					throw new Error("the server asked to retry");
				}
				if (!uploaded) {
					// Backs off and tries the files again; the day is already synced.
					throw new Error("a proof file is still uploading");
				}
			} finally {
				setSyncing(false);
			}
		};
		loop.current = startSyncLoop(cycle);
		return () => {
			loop.current?.stop();
			loop.current = null;
		};
	}, [ready, userId, held, api, appVersion, refreshOutbox]);

	const command = useCallback<Offline["command"]>(
		async (name, payload, options = {}) => {
			const queued = await outboxStore.all();
			const entry: OutboxEntry = {
				idempotencyKey: `${deviceId()}-${crypto.randomUUID()}`,
				command: name,
				payload,
				capturedAt: new Date().toISOString(),
				// Capture order, never the clock: one past the newest entry.
				seq: queued.reduce((max, e) => Math.max(max, e.seq), 0) + 1,
				state: "queued",
				attempts: 0,
				...(options.shipmentId === undefined ? {} : { shipmentId: options.shipmentId }),
			};
			await outboxStore.put(entry);
			const current = dayRef.current;
			if (options.apply !== undefined && current !== null) {
				const next = options.apply(current);
				await put("run", "today", next);
				setDay(next);
			}
			await refreshOutbox();
			loop.current?.trigger();
		},
		[refreshOutbox],
	);

	const ackAlert = useCallback<Offline["ackAlert"]>(
		async (notificationId) => {
			await command(ACK, ackPayload(notificationId, new Date()));
			const rest = ((await get<AlertAck[]>("run", "alerts")) ?? []).filter(
				(a) => a.id !== notificationId,
			);
			await put("run", "alerts", rest);
			setAlerts(rest);
		},
		[command],
	);

	const dismiss = useCallback<Offline["dismiss"]>(
		async (key) => {
			await outboxStore.remove(key);
			await refreshOutbox();
		},
		[refreshOutbox],
	);

	const signOut = useCallback(async () => {
		loop.current?.stop();
		const device = deviceId();
		// Bounded, so a dead network cannot keep a driver's data on the phone.
		await Promise.race([
			api.unregisterDevice(device).catch(() => undefined),
			new Promise((resolve) => setTimeout(resolve, 3000)),
		]);
		await purgeLocal();
		setDay(null);
		setOutbox([]);
		setWallet(null);
		setTenderOptions(null);
		setAlerts([]);
		await onSignedOut();
	}, [api, onSignedOut]);

	const discardHeld = useCallback(async () => {
		await purgeLocal();
		setOutbox([]);
		setLastSyncAt(null);
		setHeld(null);
	}, []);

	const value = useMemo<Offline>(
		() => ({
			ready,
			day,
			outbox,
			wallet,
			tenderOptions,
			alerts,
			ackAlert,
			online,
			syncing,
			lastSyncAt,
			command,
			syncNow: () => loop.current?.trigger(),
			dismiss,
			signOut,
			held,
			discardHeld,
			lost,
			notDriver,
		}),
		[
			ready,
			day,
			outbox,
			wallet,
			tenderOptions,
			alerts,
			ackAlert,
			online,
			syncing,
			lastSyncAt,
			command,
			dismiss,
			signOut,
			held,
			discardHeld,
			lost,
			notDriver,
		],
	);
	return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
}
